import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import { i18next, dialogs, customization } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import isEqual from 'lodash.isequal';
import upsell from 'src/js/modules/upsell';
import getCurrentPackageFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-current-package-feature-limits';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import { resolveVisualPreviewColor } from 'src/js/modules/website-design/helpers/resolve-visual-preview-color';
import AdvancedColorsSettingsDialog from '../components/advanced-colors-settings-dialog';
import segment from '../../analytics/segment';
import helpers from '../helpers';
import triggerIds from '../constants/trigger-ids';
import dialogTypes from '../../dialogs/constants/dialog-types';
import variableNames from '../constants/variable-names';
import { BLOCK_SETTINGS_APPEARANCE_TAB_ID } from '../../blocks/constants/common';
import supportedColorModes from '../constants/supported-color-modes';

const {
  getAdvancedColorsToReset,
  getAdvancedColorsSections,
  updatePalettesWith,
  updateCustomPalettes,
  getDefaultColorSchemeLabels,
  createPaletteEngine,
  getVariableNameTrackingId,
  updateLivePaletteElement,
  saveGlobalPalette,
} = helpers;

const { ColorTile } = designSystem;

const {
  hostingPackageFeatureNames: { ADVANCED_WEBSITE_CUSTOMIZATION },
} = subscriptionManagerConstants;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const getColorSchemeOptions = ({ colors, schemes = {} }) => {
  const defaultOptions = getDefaultColorSchemeLabels().map(([schemeId, label]) => {
    const overrides = schemes[schemeId]?.variableOverrides;
    const color = overrides?.[variableNames.BLOCK_BACKGROUND] || colors[schemeId];
    const { backgroundColor: resolvedBackground, isDarkTheme } = resolveVisualPreviewColor({
      color,
      overrides,
    });

    return {
      label,
      value: schemeId,
      iconComponent: (
        <ColorTile size="medium" backgroundColor={resolvedBackground} isDarkTheme={isDarkTheme} />
      ),
    };
  });

  const customOptions = Object.entries(schemes)
    .filter(([schemeId]) => schemeId.startsWith(customization.constants.CUSTOM_COLOR_SCHEME_PREFIX))
    .map(([schemeId, { label, baseColor, variableOverrides }]) => {
      const color = variableOverrides?.[variableNames.BLOCK_BACKGROUND] || baseColor;
      const { backgroundColor: resolvedBackground, isDarkTheme } = resolveVisualPreviewColor({
        color,
        overrides: variableOverrides,
      });
      return {
        label: i18next.t('{name} color scheme', { name: label }),
        value: schemeId,
        iconComponent: (
          <ColorTile size="medium" backgroundColor={resolvedBackground} isDarkTheme={isDarkTheme} />
        ),
      };
    });

  if (!customOptions.length) return defaultOptions;

  return [...defaultOptions, ...customOptions];
};

const getCaptions = () => ({
  title: i18next.t('Edit color scheme'),
  description: i18next.t(
    'Changes you make here will affect all blocks of your website that use this color scheme'
  ),
  breadcrumbs: [i18next.t('Design'), i18next.t('Colors')],
  tagLabel: i18next.t('PRO'),
  cancelButton: i18next.t('Cancel'),
  submitButton: i18next.t('Save'),
  searchPlaceholder: i18next.t('Search...'),
  emptyStateTitle: i18next.t('Sorry!'),
  emptyStateSubtitle: i18next.t('Nothing was found'),
});

const filterColorSections = (colorsSections, searchQuery) => {
  const normalizedQuery = searchQuery.trim().toLowerCase();
  if (!normalizedQuery) return colorsSections;

  return colorsSections
    .map((section) => {
      const isSectionFullyMatched = section.title.toLowerCase().includes(normalizedQuery);
      if (isSectionFullyMatched) {
        return section;
      }

      const matchingItems = section.colorItems.filter((item) =>
        item.title.toLowerCase().includes(normalizedQuery)
      );
      return matchingItems.length ? { ...section, colorItems: matchingItems } : null;
    })
    .filter(Boolean);
};

const AdvancedColorsSettingsContainer = ({
  colorSchemeId: initialColorSchemeId,
  scrollTop = null,
  headerTextContentVisibility = true,
  triggerId: initialTriggerId,
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
}) => {
  const {
    offsetX,
    offsetY,
    triggerId: sharedTriggerId,
    engine: sharedEngine,
    originPalette: sharedOriginPalette,
    activeColorSections: initialActiveColorSections,
    searchQuery: initialSearchQuery = '',
    isDeveloperMode,
    editingButtonPresetId,
    preservedPalette,
    fontPairId,
    sourceId: fontSettingsSourceId,
  } = getSharedData();

  const dispatch = useDispatch();
  const contentRef = useRef(null);
  const triggerId = sharedTriggerId || initialTriggerId;

  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [dialogPosition, updatePosition] = useState({ offsetX, offsetY });
  const [colorSchemeId, setColorSchemeId] = useState(initialColorSchemeId);
  const [isVisibleHeaderTextContent, setIsVisibleHeaderTextContent] = useState(
    headerTextContentVisibility
  );

  const customColorPalettes = useSelector(customization.selectors.getCustomColorPalettes);
  const buttonPreset = useSelector((state) =>
    editingButtonPresetId
      ? customization.selectors.getButtonPreset(state, editingButtonPresetId)
      : customization.accessors.getCurrentButtonPreset(state)
  );

  const [featureFlags] = useFeatureFlags(['decoration_text_formatting']);
  const { decoration_text_formatting: isTextDecorationEnabled } = featureFlags;

  const [originPalette] = useState(
    () => sharedOriginPalette || customization.accessors.getCurrentColorPalette()
  );

  const [engine] = useState(
    () => sharedEngine || createPaletteEngine(originPalette, customColorPalettes)
  );

  const [{ paletteState, paletteVariables }, setPaletteData] = useState(() => ({
    paletteState: engine.exportState(),
    paletteVariables: engine.getOutput().variables,
  }));

  const [initialPalette] = useState(() => preservedPalette || paletteState);

  const colorSchemeOptions = useMemo(() => getColorSchemeOptions(paletteState), [paletteState]);

  const colorsSections = useMemo(
    () =>
      getAdvancedColorsSections({
        variables: {
          ...paletteVariables.root,
          ...paletteVariables.schemes[colorSchemeId],
        },
        overrides: paletteState.schemes?.[colorSchemeId]?.variableOverrides || {},
        buttonPreset,
        isTextDecorationEnabled,
      }),
    [paletteVariables, colorSchemeId, paletteState, buttonPreset, isTextDecorationEnabled]
  );

  const filteredColorsSections = useMemo(
    () => filterColorSections(colorsSections, searchQuery),
    [colorsSections, searchQuery]
  );

  const defaultActiveSections = useMemo(() => colorsSections.map(({ id }) => id), [colorsSections]);

  const [userActiveSections, setUserActiveSections] = useState(initialActiveColorSections);
  const activeColorSections = userActiveSections || defaultActiveSections;

  const limits = getCurrentPackageFeatureLimits();
  const isAdvancedColorCustomizationAvailable =
    limits.advancedWebsiteCustomization.available || isDeveloperMode;
  const defaultSelectOption = colorSchemeOptions.find((option) => option.value === colorSchemeId);
  const cameFromButtonSettings =
    triggerId === triggerIds.PRIMARY_BUTTON_COLOR ||
    triggerId === triggerIds.SECONDARY_BUTTON_COLOR;
  const cameFromBlockSettings = triggerId === triggerIds.BLOCK_SETTINGS;
  const cameFromElementSettings = triggerId === triggerIds.ELEMENT_SETTINGS;
  const cameFromFontSettings = triggerId === triggerIds.EDIT_FONT_PAIR;

  const handleDragEnd = (_, { x, y }) => {
    updatePosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const updateSharedData = (customData = {}) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      ...customData,
    });
  };

  const handleColorSelect = (colorItem, variableCategory) => {
    const select = () => {
      updateSharedData({
        triggerId,
        searchQuery,
        activeColorSections,
        engine,
        originPalette,
        preservedPalette: initialPalette,
      });

      const supportedModes =
        typeof colorItem.gradient !== 'undefined'
          ? [supportedColorModes.GRADIENT, supportedColorModes.SOLID]
          : [supportedColorModes.SOLID];

      dispatch(
        dialogs.actions.show(dialogTypes.ADVANCED_COLOR_PICKER_DIALOG, {
          ...colorItem,
          colorSchemeId,
          scrollTop: cameFromFontSettings ? scrollTop : contentRef.current.scrollTop,
          headerTextContentVisibility: cameFromFontSettings
            ? headerTextContentVisibility
            : isVisibleHeaderTextContent,
          variableId: getVariableNameTrackingId(colorItem.variableName),
          variableCategory,
          supportedModes,
          triggerId: triggerIds.ADVANCED_DESIGN_SETTINGS,
          backgroundColor:
            paletteState.schemes?.[colorSchemeId]?.baseColor || paletteState.colors[colorSchemeId],
          schemeVariables: {
            ...paletteVariables.root,
            ...paletteVariables.schemes[colorSchemeId],
          },
        })
      );
    };

    if (isDeveloperMode) {
      select();
      return;
    }

    upsell.operations
      .requestFeatureAvailability(ADVANCED_WEBSITE_CUSTOMIZATION)
      .then(select)
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
      });
  };

  const handleColorReset = (variableName, variableCategory) => {
    const reset = () => {
      const colorsToReset = getAdvancedColorsToReset(variableName, buttonPreset);
      const compiledPalette = engine.resetSchemeOverrides(colorSchemeId, colorsToReset);

      updateLivePaletteElement(compiledPalette);

      setPaletteData({
        paletteState: engine.exportState(),
        paletteVariables: compiledPalette.variables,
      });

      trackEvent(events.DESIGN_SETTINGS_RESET_TRIGGER_CLICKED, {
        dialogId: dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG,
        triggerId: triggerIds.ITEM_COLOR,
        colorsAdjusted: colorsToReset,
        variableCategory,
        variableId: getVariableNameTrackingId(variableName),
      });
    };

    if (isDeveloperMode) {
      reset();
      return;
    }

    upsell.operations
      .requestFeatureAvailability(ADVANCED_WEBSITE_CUSTOMIZATION)
      .then(reset)
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
      });
  };

  const savePaletteChanges = () => {
    const updatedPalettes = updatePalettesWith(paletteState, customColorPalettes);
    updateCustomPalettes(customColorPalettes, updatedPalettes);
    saveGlobalPalette({ compiledPalette: engine.getOutput() });
  };

  const cleanSharedData = (customData = {}) => {
    updateSharedData({
      triggerId: null,
      preservedPalette: null,
      isDeveloperMode: false,
      searchQuery: '',
      activeColorSections: null,
      ...customData,
    });
  };

  const backToButtonSettings = ({ savePalette = false } = {}) => {
    if (savePalette) {
      savePaletteChanges();
    }

    cleanSharedData({
      editingButtonPresetId: null,
      engine: null,
    });

    dispatch(
      dialogs.actions.show(dialogTypes.BUTTON_PRESET_SETTINGS, {
        presetId: editingButtonPresetId,
      })
    );
  };

  const backToBlockSettings = ({ savePalette = false } = {}) => {
    if (savePalette) {
      savePaletteChanges();
    }
    cleanSharedData({ engine: null, originPalette: null });

    dispatch(
      dialogs.actions.show(dialogTypes.BLOCK_SETTINGS, {
        tabId: BLOCK_SETTINGS_APPEARANCE_TAB_ID,
      })
    );
  };

  const backToFontSettings = ({ savePalette = false } = {}) => {
    if (savePalette) {
      savePaletteChanges();
    }

    cleanSharedData({ fontPairId: null, engine: null });

    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_FONT_SETTINGS_DIALOG, {
        fontPairId,
        sourceId: fontSettingsSourceId,
        scrollTop,
        headerTextContentVisibility: isVisibleHeaderTextContent,
      })
    );
  };

  const backToCustomPaletteSettings = () => {
    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE, {
        triggerId: triggerIds.COLOR_SCHEME_SETTINGS,
        colorPreset: paletteState.id,
      })
    );
  };

  const backToElementSettings = ({ savePalette = false } = {}) => {
    if (savePalette) {
      savePaletteChanges();
    }
    cleanSharedData({ engine: null, originPalette: null });

    dispatch(dialogs.actions.show(dialogTypes.ELEMENT_SETTINGS_DIALOG));
  };

  const handleCancel = () => {
    const restoredEngine = createPaletteEngine(initialPalette);
    updateLivePaletteElement(restoredEngine.getOutput());
    onDialogCancel();

    if (cameFromButtonSettings) {
      backToButtonSettings();
      return;
    }

    if (cameFromBlockSettings) {
      backToBlockSettings();
      return;
    }

    if (cameFromElementSettings) {
      backToElementSettings();
      return;
    }

    if (cameFromFontSettings) {
      backToFontSettings();
      return;
    }

    cleanSharedData({ engine: restoredEngine });
    backToCustomPaletteSettings();
  };

  const handleSubmit = () => {
    const advancedColorAdjusted = Object.entries(paletteState.schemes || {}).some(
      ([schemeId, { variableOverrides }]) =>
        !isEqual(variableOverrides, initialPalette.schemes?.[schemeId]?.variableOverrides)
    );

    onDialogMainAction({
      colorScheme: colorSchemeId,
      advancedColorAdjusted,
    });

    if (cameFromButtonSettings) {
      backToButtonSettings({ savePalette: advancedColorAdjusted });
      return;
    }

    if (cameFromBlockSettings) {
      backToBlockSettings({ savePalette: advancedColorAdjusted });
      return;
    }

    if (cameFromElementSettings) {
      backToElementSettings({ savePalette: advancedColorAdjusted });
      return;
    }

    if (cameFromFontSettings) {
      backToFontSettings({ savePalette: advancedColorAdjusted });
      return;
    }

    cleanSharedData();
    backToCustomPaletteSettings();
  };

  const handleSearchQueryChange = (query) => {
    const filteredSections = filterColorSections(colorsSections, query);
    const searchTerm = query.trim();

    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setIsVisibleHeaderTextContent(true);

    setSearchQuery(searchTerm);

    if (searchTerm) {
      setUserActiveSections([
        ...new Set([...activeColorSections, ...filteredSections.map(({ id }) => id)]),
      ]);

      trackEvent(events.DESIGN_SETTINGS_SEARCH_PERFORMED, {
        sourceId: 'advanced-color-settings',
        searchTerm,
      });
    }
  };

  const onSearchFocus = () => {
    trackEvent(events.DESIGN_SETTINGS_SEARCH_CLICKED, {
      sourceId: 'advanced-color-settings',
    });
  };

  const handleColorSchemeChange = (option) => {
    const prevColorScheme = colorSchemeId;
    setColorSchemeId(option.value);

    trackEvent(events.DESIGN_SETTINGS_COLOR_SCHEME_CHANGED, {
      prevColorScheme,
      selectedColorScheme: option.value,
    });
  };

  useEffect(() => {
    if (!scrollTop || !contentRef.current) return;
    const timeout = bowser.safari ? 100 : 0;
    const timeoutId = setTimeout(() => contentRef.current.scrollTo(0, scrollTop), timeout);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const contentWrapper = contentRef?.current;

    if (!contentWrapper) return;

    const scrollHandler = () => {
      const scrollBottom = contentWrapper.scrollHeight - contentWrapper.clientHeight;
      const isAtBottom = contentWrapper.scrollTop >= scrollBottom - 1;

      if (activeColorSections.length < 1) {
        setIsVisibleHeaderTextContent(true);

        return;
      }

      if (isAtBottom) return; // Skip if at bottom to avoid flicker

      if (contentWrapper.scrollTop > 150) {
        setIsVisibleHeaderTextContent(false);
      } else if (contentWrapper.scrollTop < 50) {
        setIsVisibleHeaderTextContent(true);
      }
    };

    contentWrapper.addEventListener('scroll', scrollHandler);

    // eslint-disable-next-line consistent-return
    return () => {
      contentWrapper.removeEventListener('scroll', scrollHandler);
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const captions = getCaptions();

  return (
    <AdvancedColorsSettingsDialog
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      colorsSections={filteredColorsSections}
      isAdvancedColorCustomizationAvailable={isAdvancedColorCustomizationAvailable}
      contentRef={contentRef}
      initialSearchQuery={initialSearchQuery}
      isVisibleHeaderTextContent={isVisibleHeaderTextContent}
      activeAccordionItems={activeColorSections}
      onToggleAccordionItem={setUserActiveSections}
      colorSchemeOptions={colorSchemeOptions}
      defaultColorSchemeOption={defaultSelectOption}
      onColorSchemeChange={handleColorSchemeChange}
      onColorSelect={handleColorSelect}
      onColorReset={handleColorReset}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      onDragEnd={handleDragEnd}
      onSearchQueryChange={handleSearchQueryChange}
      onSearchFocus={onSearchFocus}
    />
  );
};

AdvancedColorsSettingsContainer.propTypes = {
  colorSchemeId: PropTypes.string.isRequired,
  scrollTop: PropTypes.number,
  headerTextContentVisibility: PropTypes.bool,
  triggerId: PropTypes.string,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default AdvancedColorsSettingsContainer;
